/** RSASALE / "RSA SALES" company helpers — non-RSASALE flows must stay unchanged. */

const normalizeKey = (value) =>
  String(value || '')
    .toUpperCase()
    .replace(/\s+/g, '');

export const isRsaSaleContext = (ctx = {}) => {
  const productSale = normalizeKey(ctx.productSale ?? ctx.ProductSale);
  const companyName = String(
    ctx.selectedCompanyName || ctx.companyName || ctx.name || ''
  )
    .toUpperCase()
    .trim();

  return (
    productSale === 'RSASALE' ||
    companyName === 'RSA SALES' ||
    normalizeKey(companyName) === 'RSASALES'
  );
};

export const getSessionUser = () => {
  try {
    return JSON.parse(sessionStorage.getItem('user') || '{}');
  } catch {
    return {};
  }
};

export const isRsaSaleSession = () => isRsaSaleContext(getSessionUser());

/** Find RSASALE / RSA SALES company from AllCompany list. */
export const findRsaSaleCompany = (companyDetails = []) => {
  const list = Array.isArray(companyDetails) ? companyDetails : [];
  return (
    list.find((c) =>
      isRsaSaleContext({
        productSale: c.productSale || c.ProductSale,
        companyName: c.name || c.Name || c.companyName,
        name: c.name || c.Name,
      })
    ) || null
  );
};

/**
 * For Finance (and similar roles): load RSA company into session so
 * dashboard/report reuse RSASALE APIs without breaking other roles.
 */
export const applyRsaCompanyToSession = (company) => {
  if (!company) return getSessionUser();

  const companyId =
    company.applicationUserId ||
    company.companyProfileId ||
    company.CompanyProfileId ||
    company.userId ||
    company.UserId ||
    '';
  const companyName = company.name || company.Name || company.companyName || 'RSA SALES';
  const productSale = company.productSale || company.ProductSale || 'RSASALE';

  const user = getSessionUser();
  const next = {
    ...user,
    companyId,
    companyName,
    productSale,
    selectedCompanyId: companyId,
    selectedCompanyName: companyName,
    selectedProductSale: productSale,
  };
  sessionStorage.setItem('user', JSON.stringify(next));
  return next;
};

export const normalizeAgentPackages = (rawPackages = [], forceRsaType = false) => {
  const list = Array.isArray(rawPackages) ? rawPackages : [];
  return list.map((pkg) => ({
    ...pkg,
    packageid:
      pkg.packageid ??
      pkg.packageId ??
      pkg.PackageId ??
      pkg.serviceId ??
      pkg.ServiceId,
    packageName:
      pkg.packageName ??
      pkg.PackageName ??
      pkg.serviceName ??
      pkg.ServiceName ??
      '',
    packagePrice: pkg.packagePrice ?? pkg.PackagePrice ?? pkg.price ?? pkg.Price ?? 0,
    packageCode: pkg.packageCode ?? pkg.PackageCode ?? '',
    productType:
      pkg.productType ??
      pkg.ProductType ??
      (forceRsaType ? 'RSA' : undefined),
    packageDuration: pkg.packageDuration ?? pkg.PackageDuration ?? '',
    serviceid: pkg.serviceid ?? pkg.serviceId ?? pkg.ServiceId ?? pkg.packageid,
  }));
};

export const normalizeRetailAppSaleRow = (row = {}) => ({
  ...row,
  txn_ref_no:
    row.txn_ref_no ??
    row.transactionId ??
    row.TransectionID ??
    row.TransactionID ??
    row.transactionRef ??
    row.id ??
    '-',
  bill_reference: row.bill_reference ?? row.billReference ?? '-',
  amount: row.amount ?? row.Amount ?? row.packagePrice ?? row.PackagePrice ?? '-',
  mobile_no:
    row.mobile_no ??
    row.MobileNumber ??
    row.msisdn ??
    row.mobileNo ??
    row.pp_MobileNumber,
  status: row.status ?? row.Status ?? row.responseCode ?? row.ResponseCode ?? '-',
  CustomerName:
    row.CustomerName ?? row.Name ?? row.customerName ?? row.name ?? '-',
  agent_Name:
    row.agent_Name ?? row.agentName ?? row.AgentName ?? row.agent_name ?? '-',
  company_Name:
    row.company_Name ?? row.companyName ?? row.CompanyName ?? '-',
  created_at:
    row.created_at ?? row.createdAt ?? row.CreatedAt ?? row.saleDate ?? row.SaleDate,
  Static_Response:
    row.Static_Response ?? row.message ?? row.Message ?? row.responseMessage ?? '-',
  pp_ResponseCode:
    row.pp_ResponseCode ?? row.responseCode ?? row.ResponseCode ?? '-',
});

const toNum = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const formatMoney = (value) => {
  const n = toNum(value);
  return n.toLocaleString('en-PK', { maximumFractionDigits: 0 });
};

/**
 * RetailApp_Dashboard shape:
 * { responseCode, dashboadData: { todaySales, todayRevenue, monthSales, monthRevenue,
 *   totalSales, totalRevenue, totalEarned, totalPayable }, agentWise: [...] }
 */
export const normalizeRetailAppDashboard = (payload = {}) => {
  const d =
    payload?.dashboadData ?? // API typo (real field name)
    payload?.dashboardData ??
    payload?.data ??
    payload?.summary ??
    payload ??
    {};

  const todaySales = toNum(d.todaySales ?? d.TodaySales);
  const todayRevenue = toNum(d.todayRevenue ?? d.TodayRevenue);
  const monthSales = toNum(d.monthSales ?? d.MonthSales);
  const monthRevenue = toNum(d.monthRevenue ?? d.MonthRevenue);
  const totalSales = toNum(
    d.totalSales ?? d.TotalSales ?? d.total_payments ?? d.total_count ?? d.total
  );
  const totalRevenue = toNum(d.totalRevenue ?? d.TotalRevenue);
  const totalEarned = toNum(d.totalEarned ?? d.TotalEarned);
  const totalPayable = toNum(d.totalPayable ?? d.TotalPayable);

  const agentWise = Array.isArray(payload?.agentWise)
    ? payload.agentWise
    : Array.isArray(payload?.AgentWise)
      ? payload.AgentWise
      : [];

  return {
    // RSA RetailApp metrics
    todaySales,
    todayRevenue,
    monthSales,
    monthRevenue,
    totalSales,
    totalRevenue,
    totalEarned,
    totalPayable,
    todayRevenueLabel: formatMoney(todayRevenue),
    monthRevenueLabel: formatMoney(monthRevenue),
    totalRevenueLabel: formatMoney(totalRevenue),
    totalEarnedLabel: formatMoney(totalEarned),
    totalPayableLabel: formatMoney(totalPayable),
    agentWise,

    // Legacy paybridge-compatible keys (fallback for shared UI)
    total_payments: totalSales,
    completed_count: monthSales,
    pending_count: todaySales,
    failed_count: 0,
  };
};

export const extractRetailAppReportRows = (responseData = {}) => {
  const raw =
    responseData?.data ??
    responseData?.sales ??
    responseData?.Sales ??
    responseData?.items ??
    responseData?.records ??
    responseData?.report ??
    [];
  const list = Array.isArray(raw) ? raw : [];
  return list.map(normalizeRetailAppSaleRow);
};
